import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft } from 'lucide-react';
import Logo from '@/components/Logo';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

const GCC_CODES = [
  { code: '+971', label: '🇦🇪 +971', country: 'UAE' },
  { code: '+966', label: '🇸🇦 +966', country: 'KSA' },
  { code: '+974', label: '🇶🇦 +974', country: 'Qatar' },
  { code: '+973', label: '🇧🇭 +973', country: 'Bahrain' },
  { code: '+968', label: '🇴🇲 +968', country: 'Oman' },
  { code: '+965', label: '🇰🇼 +965', country: 'Kuwait' },
];

interface AuthFlowProps {
  onSuccess?: () => void;
  prefillEmail?: string;
  compact?: boolean;
}

export default function AuthFlow({ onSuccess, prefillEmail, compact }: AuthFlowProps) {
  const [step, setStep] = useState<'identifier' | 'otp'>('identifier');
  const [tab, setTab] = useState<'email' | 'phone'>('email');
  const [email, setEmail] = useState(prefillEmail || '');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+971');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isNewUser, setIsNewUser] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  const { sendOtp, verifyOtp, user } = useAuth();
  const { toast } = useToast();

  // If user becomes authenticated, call onSuccess
  useEffect(() => {
    if (user && step === 'otp') {
      onSuccess?.();
    }
  }, [user, step, onSuccess]);

  // Prefill email if provided
  useEffect(() => {
    if (prefillEmail) {
      setEmail(prefillEmail);
      setTab('email');
    }
  }, [prefillEmail]);

  const startResendTimer = useCallback(() => {
    setResendTimer(30);
    timerRef.current = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const identifier = tab === 'email' ? email : `${countryCode}${phone}`;
  const displayIdentifier = tab === 'email' ? email : `${countryCode} ${phone}`;

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.trim()) return;
    setIsLoading(true);

    // Detect new vs returning user before sending OTP
    try {
      if (tab === 'email') {
        const { data } = await supabase.from('users').select('id').eq('email', email).maybeSingle();
        setIsNewUser(!data);
      } else {
        const fullPhone = `${countryCode}${phone}`;
        const { data } = await supabase.from('users').select('id').eq('phone', fullPhone).maybeSingle();
        setIsNewUser(!data);
      }
    } catch {
      // Default to new user if check fails
      setIsNewUser(true);
    }

    setTermsAccepted(false);
    const { error } = await sendOtp(identifier, tab);
    setIsLoading(false);

    if (error) {
      toast({ title: 'Could not send code', description: error.message, variant: 'destructive' });
    } else {
      setStep('otp');
      startResendTimer();
      toast({ title: 'Code sent', description: `Check your ${tab === 'email' ? 'inbox' : 'messages'}` });
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length < 6) return;
    if (isNewUser && !termsAccepted) return;
    setIsLoading(true);
    const { data, error } = await verifyOtp(identifier, otp, tab);
    setIsLoading(false);

    if (error) {
      toast({ title: 'Verification failed', description: error.message, variant: 'destructive' });
    } else {
      // Log consent for new users
      if (isNewUser && data?.user) {
        supabase.from('consents').insert({
          user_id: data.user.id,
          kind: 'terms_and_privacy',
          granted: true,
        }).then(() => {});
      }
      toast({ title: 'Welcome to Flexaura!', description: 'You\'re now signed in.' });
      onSuccess?.();
    }
  };

  const handleResend = async () => {
    setIsLoading(true);
    const { error } = await sendOtp(identifier, tab);
    setIsLoading(false);
    if (!error) {
      startResendTimer();
      toast({ title: 'Code resent' });
    }
  };

  const goBack = () => {
    setStep('identifier');
    setOtp('');
    if (timerRef.current) clearInterval(timerRef.current);
    setResendTimer(0);
  };

  if (step === 'otp') {
    return (
      <div className="w-full space-y-6">
        {!compact && (
          <div className="text-center">
            <Logo className="mx-auto mb-4" />
          </div>
        )}
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-heading font-semibold text-primary">Enter your code</h2>
          <p className="text-sm text-muted-foreground font-body">
            We sent a 6-digit code to <span className="font-medium text-foreground">{displayIdentifier}</span>
          </p>
        </div>

        <form onSubmit={handleVerify} className="space-y-6">
          <div className="flex justify-center">
            <InputOTP
              maxLength={6}
              value={otp}
              onChange={setOtp}
              className="font-mono"
            >
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map(i => (
                  <InputOTPSlot
                    key={i}
                    index={i}
                    className="w-12 h-14 text-lg font-mono border-primary/30 focus-within:ring-accent focus-within:border-accent"
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>

          {isNewUser && (
            <div className="flex items-start gap-2">
              <Checkbox
                id="terms-consent"
                checked={termsAccepted}
                onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                className="mt-0.5"
              />
              <label htmlFor="terms-consent" className="text-xs text-muted-foreground font-body leading-tight cursor-pointer">
                I agree to Flexaura's{' '}
                <Link to="/terms" target="_blank" className="underline hover:text-foreground">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank" className="underline hover:text-foreground">Privacy Policy</Link>
                , and confirm I am 18 or over.
              </label>
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-body font-semibold"
            disabled={otp.length < 6 || isLoading || (isNewUser && !termsAccepted)}
          >
            {isLoading ? 'Verifying...' : 'Verify Code'}
          </Button>
        </form>

        <div className="flex items-center justify-between text-sm">
          <button
            onClick={goBack}
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground font-body transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Change {tab === 'email' ? 'email' : 'phone'}
          </button>

          {resendTimer > 0 ? (
            <span className="text-muted-foreground font-body">
              Resend in {resendTimer}s
            </span>
          ) : (
            <button
              onClick={handleResend}
              disabled={isLoading}
              className="text-primary hover:text-primary/80 font-body font-medium transition-colors"
            >
              Resend code
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {!compact && (
        <div className="text-center">
          <Logo className="mx-auto mb-4" />
        </div>
      )}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-heading font-semibold text-primary">Welcome to Flexaura</h2>
        <p className="text-sm text-muted-foreground font-body">Sign in or create your account</p>
      </div>

      <Tabs value={tab} onValueChange={v => setTab(v as 'email' | 'phone')} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="email" className="font-body">Email</TabsTrigger>
          <TabsTrigger value="phone" className="font-body">Phone</TabsTrigger>
        </TabsList>

        <TabsContent value="email" className="mt-4">
          <form onSubmit={handleSendOtp} className="space-y-4">
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              disabled={isLoading}
              className="font-body"
            />
            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-body font-semibold"
              disabled={isLoading || !email.trim()}
            >
              {isLoading ? 'Sending...' : 'Send One-Time Code'}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="phone" className="mt-4">
          <form onSubmit={handleSendOtp} className="space-y-4">
            <div className="flex gap-2">
              <Select value={countryCode} onValueChange={setCountryCode}>
                <SelectTrigger className="w-[130px] font-body">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GCC_CODES.map(c => (
                    <SelectItem key={c.code} value={c.code} className="font-body">
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="tel"
                placeholder="50 123 4567"
                value={phone}
                onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
                required
                disabled={isLoading}
                className="flex-1 font-body"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-body font-semibold"
              disabled={isLoading || !phone.trim()}
            >
              {isLoading ? 'Sending...' : 'Send One-Time Code'}
            </Button>
          </form>
        </TabsContent>
      </Tabs>

      <p className="text-xs text-center text-muted-foreground font-body">
        By continuing you agree to our{' '}
        <Link to="/terms" className="underline hover:text-foreground">Terms of Service</Link>
        {' '}and{' '}
        <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>
      </p>
    </div>
  );
}
